import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { formatDate } from '../../lib/format.js';

export interface ProductStatusTabProps {
  status: string;
  publishedAt: string | null;
  onArchive: () => void;
  onActivate: () => void;
  isBusy: boolean;
}

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'warning'> = {
  draft: 'warning',
  active: 'success',
  archived: 'neutral',
};

/**
 * Draft/archived products are never publicly visible -- `status` alone
 * controls that (see docs/catalog-architecture.md "Product lifecycle");
 * there is deliberately no second "published" flag here to keep in sync.
 */
export function ProductStatusTab({ status, publishedAt, onArchive, onActivate, isBusy }: ProductStatusTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600">Current status:</span>
        <AdminStatusBadge label={status} tone={STATUS_TONE[status] ?? 'neutral'} />
      </div>
      <p className="text-sm text-gray-500">
        {publishedAt
          ? `First published ${formatDate(publishedAt)}.`
          : 'This product has never been published.'}
      </p>
      <div className="flex gap-2">
        {status !== 'active' && (
          <button
            type="button"
            onClick={onActivate}
            disabled={isBusy}
            className="bg-brand text-brand-foreground rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60"
          >
            Activate (make public)
          </button>
        )}
        {status !== 'archived' && (
          <button
            type="button"
            onClick={onArchive}
            disabled={isBusy}
            className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Archive (hide from customers)
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400">
        Archiving never deletes this product -- variants, price history, slug history, and inventory
        movements all stay intact and it can be reactivated later.
      </p>
    </div>
  );
}
